import type { ReactNode } from 'react';

/**
 * tone별 연배경 + 본색 텍스트 — 목업 `--<tone>-bg` / `--<tone>` 짝 그대로.
 * Tailwind 정적 스캔 대상이므로 완성형 리터럴로 둔다(동적 조합 금지).
 */
const TONE_FILL = {
  blue: 'bg-blue-50 text-blue-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  // orange는 목업 6색 밖이지만, rose와 amber 사이에 층이 없으면 심각도 4단계
  // (CRITICAL/HIGH/MEDIUM/LOW)에서 HIGH와 MEDIUM이 같은 색으로 뭉쳐 정보가 사라진다.
  // 색을 늘리는 비용보다 등급이 구분되지 않는 손실이 크다 — 2026-07-28 사용자 확정.
  orange: 'bg-orange-50 text-orange-600',
  rose: 'bg-rose-50 text-rose-600',
  violet: 'bg-violet-50 text-violet-600',
  // teal은 목업 6색에는 없지만, 분류 색이 7색을 넘겨 서로 다른 유형이 같은 색으로 뭉치는
  // 것을 막기 위해 둔다(TG 완료증명 ↔ TBM 충돌 해소 — 2026-07-27 색 단일화).
  teal: 'bg-teal-50 text-teal-600',
  slate: 'bg-ink-100 text-ink-600',
  brand: 'bg-brand-50 text-brand-700',
} as const;

/** 테두리 색(목업 `--<tone>-ring`). 알약(상태 배지)에서만 쓴다 — 아래 BOX_CLASS 주석 참조. */
const TONE_RING = {
  blue: 'ring-blue-200',
  emerald: 'ring-emerald-200',
  amber: 'ring-amber-200',
  orange: 'ring-orange-200',
  rose: 'ring-rose-200',
  violet: 'ring-violet-200',
  teal: 'ring-teal-200',
  slate: 'ring-ink-300',
  brand: 'ring-brand-100',
} as const;

/** dot 색은 텍스트 색과 같은 계열의 진한 값. */
const DOT_CLASS = {
  blue: 'bg-blue-600',
  emerald: 'bg-emerald-600',
  amber: 'bg-amber-600',
  orange: 'bg-orange-600',
  rose: 'bg-rose-600',
  violet: 'bg-violet-600',
  teal: 'bg-teal-600',
  slate: 'bg-ink-500',
  brand: 'bg-brand-600',
} as const;

/**
 * 모양·크기는 prop으로만 바꾼다 — `className`에 rounded / px / text 유틸을 덧붙이면 Tailwind
 * 생성 CSS 순서에 따라 어느 쪽이 이길지 보장되지 않아 조용히 무시될 수 있다.
 *
 * 값은 목업 9종 공통 규격이다.
 * - 알약(`pill`) = `.status`: 13.5px/650, `padding:5px 10px 5px 8px`, `border:1px solid <ring>`
 * - 사각(`rounded`) = `.type-pill`: 13px/700, `padding:4px 10px`, radius 6px,
 *   `border:1px solid transparent`(= **테두리 없음**)
 *
 * 글자 크기는 13px 하나로 스냅했다. 목업의 상태 배지가 `.status` 13.5 / `.status-pill` 13 /
 * `.mst`·`.ss` 12.5로 스스로 갈려 있어(같은 역할, 다른 값 = 저작 습관) 그대로 옮길 근거가 없고,
 * 9종 공통인 `.type-pill` 13px과 일치시키면 두 모양이 한 층에 놓인다.
 * 굵기도 같은 이유로 700 하나 — 650과 700 사이에서 Tailwind가 주는 600/700 중 오차가 작은 쪽.
 *
 * **테두리는 별도 축이 아니라 모양에 딸린 값이다.** 목업이 상태 배지에만 tone 테두리를 주고
 * 분류 필은 투명 테두리로 두기 때문이며, 실코드도 87%가 테두리 없는 분류 태그다.
 */
const BOX_CLASS = {
  'md-pill': 'rounded-full text-[13px] py-[5px] px-2.5 gap-1.5 ring-1',
  'md-pill-dot': 'rounded-full text-[13px] py-[5px] pl-2 pr-2.5 gap-1.5 ring-1',
  'md-rounded': 'rounded-md text-[13px] py-1 px-2.5 gap-[5px]',
  'md-rounded-dot': 'rounded-md text-[13px] py-1 px-2.5 gap-[5px]',
  'sm-pill': 'rounded-full text-[11.5px] py-[3px] px-2 gap-1 ring-1',
  'sm-pill-dot': 'rounded-full text-[11.5px] py-[3px] pl-1.5 pr-2 gap-1 ring-1',
  'sm-rounded': 'rounded-md text-[11.5px] py-[3px] px-2 gap-1',
  'sm-rounded-dot': 'rounded-md text-[11.5px] py-[3px] px-2 gap-1',
} as const;

/** dot 지름 — 목업 `.status .dot` 6px / `.type-pill .d` 5px. */
const DOT_SIZE_CLASS = {
  pill: 'h-1.5 w-1.5',
  rounded: 'h-[5px] w-[5px]',
} as const;

export type BadgeTone = keyof typeof TONE_FILL;
export type BadgeShape = 'pill' | 'rounded';
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps {
  /** 색 계열. 기본 slate(중립). 상태 의미는 소비처가 매핑한다. */
  tone?: BadgeTone;
  /** 모양. 기본 pill(상태 배지), 유형 분류 필은 rounded. */
  shape?: BadgeShape;
  /**
   * 크기. 기본 md(13px). sm(11.5px)은 좁은 칸에 곁들이는 보조 라벨용 —
   * 목업 `.appr-sub .type-pill`·`.insp-tab .count`·`.risk-header .count-pill`이 이 층이다.
   */
  size?: BadgeSize;
  /** 앞쪽 점 표시 — 상태 배지에서 색만으로 구분되지 않게 돕는 시각 보조. */
  dot?: boolean;
  /** 라벨. */
  children: ReactNode;
  /** 추가 클래스. */
  className?: string;
}

/**
 * 공용 배지 — 상태·분류 라벨의 단일 소스.
 *
 * 테두리를 border가 아닌 ring으로 그리는 이유는 ring이 레이아웃 박스를 넓히지 않아
 * 표 셀·행 안에 인라인으로 놓아도 정렬이 밀리지 않기 때문.
 *
 * tone은 **색 이름**이지 상태 이름이 아니다(도메인 상태→tone 매핑은 소비처 책임).
 * 코어에 도메인 상태를 넣지 않는 규약: [[feedback-composer-no-domain-specific-widget-in-core]].
 *
 * `className`으로 덮어쓸 수 있는 것/없는 것:
 * - **prop 전용**(같은 CSS 속성을 다툼): 여백·라운드·글자 크기·굵기·색·테두리
 * - 자유: 위치·여백 바깥값(`ml-2`·`absolute`)·`flex-none`·`justify-center`·`min-w-*`·
 *   `tabular-nums` 등. 카운트 배지는 `className="justify-center min-w-[22px] tabular-nums"`로
 *   목업 `.tab .count` 규격이 된다(전용 prop을 두지 않은 이유).
 */
export function Badge({
  tone = 'slate',
  shape = 'pill',
  size = 'md',
  dot = false,
  children,
  className = '',
}: BadgeProps) {
  const boxKey = `${size}-${shape}${dot ? '-dot' : ''}` as keyof typeof BOX_CLASS;
  const ring = shape === 'pill' ? ` ${TONE_RING[tone]}` : '';
  return (
    <span
      className={`inline-flex items-center font-bold tracking-[-0.005em] ${BOX_CLASS[boxKey]} ${TONE_FILL[tone]}${ring} ${className}`}
    >
      {dot && (
        <span
          aria-hidden="true"
          className={`flex-none rounded-full ${DOT_SIZE_CLASS[shape]} ${DOT_CLASS[tone]}`}
        />
      )}
      {children}
    </span>
  );
}
